<template>
    <div class="postback-checker">
        <p class="text-sm text-gray-400 mb-4 max-w-3xl">
            Answers "can this publisher receive a postback?" without sending anything.
            Pick a site link and it shows where a registration, FTD or CPA event would
            be forwarded, and what the URL would look like.
        </p>

        <!-- Picker: publisher -> link -->
        <div class="flex items-end gap-3 mb-4 flex-wrap">
            <div class="flex flex-col gap-1">
                <label class="text-xs text-gray-400">Publisher</label>
                <Select
                    v-model="selectedUserId"
                    :options="publisherOptions"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select a publisher"
                    class="w-80"
                    filter
                    :loading="loadingPublishers"
                />
            </div>

            <div class="flex flex-col gap-1">
                <label class="text-xs text-gray-400">Site link</label>
                <Select
                    v-model="selectedLinkId"
                    :options="linkOptions"
                    optionLabel="label"
                    optionValue="value"
                    :placeholder="selectedUserId ? 'Select a link' : 'Pick a publisher first'"
                    class="w-[28rem]"
                    filter
                    :disabled="!selectedUserId"
                    :loading="loadingLinks"
                />
            </div>

            <Button
                label="Check"
                icon="pi pi-search"
                :disabled="!selectedLinkId || loadingPreview"
                :loading="loadingPreview"
                @click="runCheck"
            />
        </div>

        <ProgressSpinner v-if="loadingPreview" style="width: 40px; height: 40px" />

        <div v-else-if="error" class="err">{{ error }}</div>

        <template v-else-if="preview">
            <!-- Routing: if this fails, no postback can ever arrive, whatever
                 URLs are configured. Shown first for that reason. -->
            <div :class="['routing', preview.routing.routable ? 'ok' : 'bad']">
                <div class="routing-head">
                    <i :class="['pi', preview.routing.routable ? 'pi-check-circle' : 'pi-times-circle']" />
                    <strong>
                        {{ preview.routing.routable
                            ? 'This link can receive postbacks'
                            : 'This link cannot receive postbacks at all' }}
                    </strong>
                </div>
                <div class="routing-body">
                    <span><span class="k">salid</span> {{ preview.routing.salid ?? '— missing —' }}</span>
                    <span><span class="k">campaignId</span> {{ preview.routing.campaignId ?? '— missing —' }}</span>
                </div>
                <p v-if="!preview.routing.routable" class="routing-why">
                    Incoming postbacks are matched on the affiliate system's salid plus the
                    link's internal note. With either missing, nothing routes here — fix that
                    before looking at the URLs below.
                </p>
            </div>

            <div class="ctx">
                {{ preview.siteBrandLink.advertiser }} — {{ preview.siteBrandLink.publisher }}
                <span v-if="preview.siteBrandLink.market">({{ preview.siteBrandLink.market }})</span>
                <span class="text-xs text-gray-500 ml-2">test click id: {{ preview.clickId }}</span>
            </div>

            <div v-for="ev in preview.events" :key="ev.eventType" class="event">
                <div class="event-head">
                    <Tag :severity="ev.ready ? 'success' : (ev.source === 'none' ? 'danger' : 'warn')"
                        :value="ev.label" />
                    <span class="event-verdict">{{ ev.verdict }}</span>
                    <Tag v-if="ev.source !== 'none'" severity="secondary"
                        :value="ev.source === 'link' ? 'per-link' : 'global fallback'" class="ml-auto" />
                </div>

                <div v-if="ev.rawUrl" class="urls">
                    <div class="url-row">
                        <span class="url-label">Configured</span>
                        <code>{{ ev.rawUrl }}</code>
                    </div>
                    <div class="url-row">
                        <span class="url-label">Would send</span>
                        <code :class="{ 'has-gap': ev.unresolvedPlaceholders.length }">{{ ev.resolvedUrl }}</code>
                        <Button icon="pi pi-copy" text rounded size="small"
                            v-tooltip.left="'Copy'"
                            @click="copy(ev.resolvedUrl)" />
                    </div>
                    <p v-if="ev.unresolvedPlaceholders.length" class="gap-note">
                        {{ formatPlaceholders(ev.unresolvedPlaceholders) }}
                        would arrive at the publisher literally, unsubstituted.
                    </p>
                </div>
            </div>
        </template>

        <Toast />
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import Select from 'primevue/select';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Toast from 'primevue/toast';
import ProgressSpinner from 'primevue/progressspinner';
import { useToast } from 'primevue/usetoast';
import PostbackService from '@/services/PostbackService';

const toast = useToast();

// One flat list of every linked site link, fetched once. Both dropdowns are
// derived from it — there is no per-publisher round trip, so switching
// publisher is instant.
const allLinks = ref([]);
const preview = ref(null);
const error = ref('');

const loadingPublishers = ref(false);
const loadingLinks = ref(false);
const loadingPreview = ref(false);

const selectedUserId = ref(null);
const selectedLinkId = ref(null);

const publisherOptions = computed(() => {
    const byId = new Map();
    for (const l of allLinks.value) {
        if (l.publisherId && !byId.has(l.publisherId)) {
            byId.set(l.publisherId, { label: l.publisher, value: l.publisherId });
        }
    }
    return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label));
});

const linkOptions = computed(() => allLinks.value
    .filter(l => l.publisherId === selectedUserId.value)
    .map(l => ({
        label: `${l.advertiser} · ${l.market}`
            + (l.internalNote ? ` · ${l.internalNote}` : ' · (no internal note)'),
        value: l.id,
    })));

async function loadLinks() {
    loadingPublishers.value = true;
    loadingLinks.value = true;
    try {
        const { data } = await PostbackService.getTestableLinks();
        allLinks.value = data?.links || [];
    } catch (err) {
        console.error('Error loading testable links:', err);
        toast.add({ severity: 'error', summary: 'Could not load site links', life: 4000 });
    } finally {
        loadingPublishers.value = false;
        loadingLinks.value = false;
    }
}

async function runCheck() {
    if (!selectedLinkId.value) return;
    loadingPreview.value = true;
    error.value = '';
    preview.value = null;
    try {
        const { data } = await PostbackService.getTestPreview(selectedLinkId.value);
        preview.value = data;
    } catch (err) {
        console.error('Error running postback check:', err);
        error.value = err?.response?.data?.message || 'Could not build the preview.';
    } finally {
        loadingPreview.value = false;
    }
}

function copy(text) {
    navigator.clipboard.writeText(text);
    toast.add({ severity: 'success', summary: 'Copied', life: 1500 });
}

// Kept out of the template on purpose: a template literal containing braces
// ({${p}}) inside a mustache fails Vue's expression parser.
function formatPlaceholders(names) {
    return (names || []).map(n => '{' + n + '}').join(', ');
}

// Changing publisher invalidates the link selection and any preview built from it.
watch(selectedUserId, () => {
    selectedLinkId.value = null;
    preview.value = null;
    error.value = '';
});

watch(selectedLinkId, () => {
    preview.value = null;
    error.value = '';
});

loadLinks();
</script>

<style scoped>
.postback-checker { padding-bottom: 2rem; }

.err {
    border-left: 2px solid #ef4444;
    background: rgba(239, 68, 68, 0.08);
    padding: 0.75rem 1rem;
    border-radius: 0 6px 6px 0;
    font-size: 0.875rem;
}

.routing {
    border: 1px solid var(--p-content-border-color, #3f3f46);
    border-left-width: 3px;
    border-radius: 0 6px 6px 0;
    padding: 0.75rem 1rem;
    margin-bottom: 1rem;
}
.routing.ok  { border-left-color: #22c55e; }
.routing.bad { border-left-color: #ef4444; }

.routing-head { display: flex; align-items: center; gap: 0.5rem; font-size: 0.9rem; }
.routing.ok .pi  { color: #86efac; }
.routing.bad .pi { color: #fca5a5; }

.routing-body {
    display: flex;
    gap: 1.5rem;
    margin-top: 0.4rem;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.8rem;
    flex-wrap: wrap;
}
.routing-body .k { opacity: 0.55; margin-right: 0.35rem; }
.routing-why { margin: 0.5rem 0 0; font-size: 0.8rem; opacity: 0.75; max-width: 60ch; }

.ctx { font-size: 0.95rem; margin-bottom: 0.75rem; }

.event {
    border: 1px solid var(--p-content-border-color, #3f3f46);
    border-radius: 6px;
    margin-bottom: 0.75rem;
    overflow: hidden;
}

.event-head {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.6rem 0.85rem;
    background: rgba(255, 255, 255, 0.03);
    flex-wrap: wrap;
}

.event-verdict { font-size: 0.85rem; }

.urls { padding: 0.6rem 0.85rem; display: flex; flex-direction: column; gap: 0.45rem; }

.url-row { display: flex; align-items: center; gap: 0.6rem; }

.url-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    opacity: 0.55;
    width: 5.5rem;
    flex: none;
}

.url-row code {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.78rem;
    background: rgba(255, 255, 255, 0.04);
    padding: 0.3rem 0.5rem;
    border-radius: 4px;
    overflow-x: auto;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
}

.url-row code.has-gap { color: #fcd34d; }

.gap-note { margin: 0; font-size: 0.75rem; color: #fcd34d; opacity: 0.9; }
</style>
